import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  Clock,
  Building2,
  Send,
  CheckCircle2,
  ChevronLeft,
  MessageSquare,
  ShieldCheck,
} from "lucide-react";
import { Logo } from "../../components/Logo";
import { Footer } from "../../sections/Footer";

/**
 * Contact page — professional two-column layout.
 * Left column: contact rails (email, response time, company, why write).
 * Right column: prominent form card with clear labels and a11y hooks.
 * Uses semantic landmarks + aria-live so screen readers announce success.
 */
export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: "General inquiry",
    message: "",
  });
  const [sent, setSent] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(
      `[EVIQ Contact] ${form.topic} — ${form.name || "no-name"}`
    );
    const body = encodeURIComponent(
      `From: ${form.name} <${form.email}>\nTopic: ${form.topic}\n\n${form.message}\n\n—\nSent from eviq.pro/contact`
    );
    window.location.href = `mailto:support@eviq.pro?subject=${subject}&body=${body}`;
    setSent(true);
  };

  return (
    <div
      className="min-h-screen bg-black text-white"
      data-testid="contact-page"
    >
      {/* Skip link for keyboard/screen-reader users */}
      <a
        href="#contact-main"
        className="skip-to-content"
        data-testid="skip-to-content"
      >
        Skip to main content
      </a>

      {/* Slim, quiet header */}
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm font-medium"
            data-testid="contact-back-home"
            aria-label="Back to homepage"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={2.5} aria-hidden="true" />
            Back
          </Link>
          <Link to="/" aria-label="EVIQ home" className="flex items-center">
            <Logo size={22} />
          </Link>
          <div className="w-16" aria-hidden="true" />
        </div>
      </header>

      <main
        id="contact-main"
        tabIndex="-1"
        className="max-w-6xl mx-auto px-5 sm:px-8 pt-8 sm:pt-12 pb-10"
      >
        {/* Page heading */}
        <div className="max-w-2xl">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400 mb-3">
            Company · Contact
          </div>
          <h1
            className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight leading-[1.1]"
            data-testid="contact-heading"
          >
            Get in touch with{" "}
            <span className="brand-text-gradient">EVIQ</span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-zinc-300 leading-relaxed">
            Questions, feedback, or subscription help — email is the fastest way to reach us. We reply within one business day.
          </p>
        </div>

        {/* Two-column grid: info rails + form */}
        <div className="mt-8 sm:mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_1.6fr] gap-6 lg:gap-10">
          {/* LEFT — Contact rails */}
          <aside aria-label="Contact information" className="space-y-2.5">
            <InfoCard
              icon={Mail}
              testId="contact-info-support"
              label="General & support"
              value="support@eviq.pro"
              href="mailto:support@eviq.pro"
            />
            <InfoCard
              icon={ShieldCheck}
              testId="contact-info-privacy"
              label="Privacy & data requests"
              value="support@eviq.pro"
              href="mailto:support@eviq.pro"
            />
            <InfoCard
              icon={Clock}
              testId="contact-info-hours"
              label="Response time"
              value="Within 1 business day"
              sub="Mon–Fri · 9am–6pm PT"
            />
            <InfoCard
              icon={Building2}
              testId="contact-info-company"
              label="Company"
              value="EVIQ LLC"
              sub="Tampa, FL USA"
            />
          </aside>

          {/* RIGHT — Form card */}
          <section
            aria-labelledby="contact-form-heading"
            className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-white/[0.01] p-5 sm:p-6 shadow-[0_20px_60px_-30px_rgba(57,255,20,0.15)]"
            data-testid="contact-form-card"
          >
            <div className="flex items-start gap-3 mb-4">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border border-white/10"
                style={{ background: "rgba(57,255,20,0.08)" }}
                aria-hidden="true"
              >
                <MessageSquare
                  className="w-4 h-4"
                  style={{ color: "#39FF14" }}
                  strokeWidth={2}
                />
              </div>
              <div>
                <h2
                  id="contact-form-heading"
                  className="font-display text-xl sm:text-2xl font-semibold text-white tracking-tight"
                >
                  Send us a message
                </h2>
                <p className="mt-0.5 text-xs sm:text-sm text-zinc-400">
                  Opens your email client. Or write to{" "}
                  <a
                    href="mailto:support@eviq.pro"
                    className="text-[#00E5FF] underline underline-offset-2 hover:text-white"
                  >
                    support@eviq.pro
                  </a>
                  .
                </p>
              </div>
            </div>

            {/* aria-live success region */}
            <div aria-live="polite" aria-atomic="true">
              {sent && (
                <div
                  data-testid="contact-form-sent"
                  role="status"
                  className="mb-4 rounded-xl border border-[#39FF14]/30 bg-[#39FF14]/[0.06] px-3 py-2.5 flex items-start gap-2 text-xs sm:text-sm text-white"
                >
                  <CheckCircle2
                    className="w-4 h-4 mt-0.5 shrink-0"
                    style={{ color: "#39FF14" }}
                    strokeWidth={2.5}
                    aria-hidden="true"
                  />
                  Your mail client should have opened with the message ready to send.
                </div>
              )}
            </div>

            <form
              onSubmit={onSubmit}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              noValidate
            >
              <Field label="Full name" htmlFor="contact-name" required>
                <input
                  id="contact-name"
                  type="text"
                  required
                  autoComplete="name"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  data-testid="contact-name"
                  aria-required="true"
                  placeholder="Jane Doe"
                  className="w-full bg-black border border-white/15 hover:border-white/30 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#39FF14] focus:ring-2 focus:ring-[#39FF14]/30 transition-colors"
                />
              </Field>

              <Field label="Email address" htmlFor="contact-email" required>
                <input
                  id="contact-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  data-testid="contact-email"
                  aria-required="true"
                  placeholder="you@example.com"
                  className="w-full bg-black border border-white/15 hover:border-white/30 rounded-lg px-3 py-2.5 text-white text-sm font-mono placeholder:text-zinc-600 focus:outline-none focus:border-[#39FF14] focus:ring-2 focus:ring-[#39FF14]/30 transition-colors"
                />
              </Field>

              <Field
                label="What's this about?"
                htmlFor="contact-topic"
                className="sm:col-span-2"
              >
                <select
                  id="contact-topic"
                  value={form.topic}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, topic: e.target.value }))
                  }
                  data-testid="contact-topic"
                  className="w-full bg-black border border-white/15 hover:border-white/30 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#39FF14] focus:ring-2 focus:ring-[#39FF14]/30 transition-colors"
                >
                  <option>General inquiry</option>
                  <option>Subscription help</option>
                  <option>Cancel my subscription</option>
                  <option>Refund request</option>
                  <option>Privacy / data request</option>
                  <option>Tesla OAuth issue</option>
                  <option>Press &amp; media</option>
                  <option>Partnership</option>
                </select>
              </Field>

              <Field
                label="Your message"
                htmlFor="contact-message"
                required
                className="sm:col-span-2"
              >
                <textarea
                  id="contact-message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, message: e.target.value }))
                  }
                  data-testid="contact-message"
                  aria-required="true"
                  placeholder="Include your VIN or account email if this is a support request…"
                  className="w-full bg-black border border-white/15 hover:border-white/30 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 leading-relaxed focus:outline-none focus:border-[#39FF14] focus:ring-2 focus:ring-[#39FF14]/30 transition-colors resize-y min-h-[96px]"
                />
              </Field>

              <div className="sm:col-span-2 flex flex-col sm:flex-row items-center gap-3 justify-between pt-1">
                <p className="text-[11px] text-zinc-300">
                  By submitting you agree to our{" "}
                  <Link
                    to="/privacy"
                    className="text-white underline underline-offset-2 hover:text-[#39FF14]"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  className="brand-gradient text-black font-semibold rounded-lg px-5 py-2.5 text-sm inline-flex items-center gap-2 hover:opacity-90 transition-opacity w-full sm:w-auto justify-center"
                >
                  Send message
                  <Send className="w-4 h-4" strokeWidth={2.5} aria-hidden="true" />
                </button>
              </div>
            </form>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

/* ---------- helpers ---------- */

const InfoCard = ({ icon: Icon, label, value, sub, href, testId }) => {
  const inner = (
    <div
      className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5 hover:bg-white/[0.05] hover:border-white/25 transition-colors"
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border border-white/10"
          style={{ background: "rgba(0,229,255,0.08)" }}
          aria-hidden="true"
        >
          <Icon
            className="w-4 h-4"
            style={{ color: "#00E5FF" }}
            strokeWidth={2}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-400 mb-0.5">
            {label}
          </div>
          <div className="text-sm text-white font-medium break-words leading-snug">
            {value}
          </div>
          {sub && <div className="text-[11px] text-zinc-300 mt-0.5 leading-snug">{sub}</div>}
        </div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block rounded-xl" aria-label={`${label}: ${value}`}>
      {inner}
    </a>
  ) : (
    inner
  );
};

const Field = ({ label, htmlFor, children, required, className = "" }) => (
  <div className={className}>
    <label htmlFor={htmlFor} className="block">
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-300 mb-1.5">
        {label}
        {required && (
          <span className="text-[#FF6B57] ml-1" aria-hidden="true">
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </div>
    </label>
    {children}
  </div>
);
